import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import { registerUser, loginUser, forgotPassword } from "../services/api";
import { useAuthStore } from "../store/authStore";
import { useNavigate } from "react-router-dom";

export default function AuthModal({ onClose }) {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("login");

  // -------------------------
  // LOGIN STATE
  // -------------------------
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // -------------------------
  // REGISTER STATE
  // -------------------------
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const setAuth = useAuthStore((state) => state.setAuth);

  // -------------------------
  // REGISTER CHANGE
  // -------------------------
  const handleRegisterChange = (e) => {
    const { name, value } = e.target;

    // Phone: numbers only, maximum 10 digits
    if (name === "phone") {
      const numbersOnly = value.replace(/\D/g, "");

      if (numbersOnly.length <= 10) {
        setRegisterData((prev) => ({
          ...prev,
          phone: numbersOnly,
        }));
      }

      return;
    }

    setRegisterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // -------------------------
  // REGISTER
  // -------------------------
  const handleRegister = async (e) => {
    e.preventDefault();

    setError("");
    setMessage("");

    const { name, email, password, phone } = registerData;

    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!phone.trim()) {
      setError("Phone number is required");
      return;
    }

    if (!/^[6-9]\d{9}$/.test(phone)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!password) {
      setError("Password is required");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      setLoading(true);

      const res = await registerUser({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        phone,
      });

      setMessage(res.message || "Registration successful!");

      setRegisterData({
        name: "",
        email: "",
        password: "",
        phone: "",
      });
    } catch (err) {
      console.error("Registration error:", err);

      setError(
        err.response?.data?.message || "Registration failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // LOGIN
  // -------------------------
  const handleLogin = async (e) => {
    e.preventDefault();

    setError("");
    setMessage("");

    const email = loginEmail.trim().toLowerCase();
    const password = loginPassword;

    // Email validation
    if (!email) {
      setError("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    // Password validation
    if (!password) {
      setError("Password is required");
      return;
    }

    try {
      setLoading(true);

      const res = await loginUser({
        email,
        password,
      });

      if (res.success) {
        // Access token stays in memory
        setAuth(res.accessToken, res.user);

        setMessage("Login successful!");
        window.location.href = "/profile";
        console.log("Logged in user:", res.user);

        // For now, close modal after successful login
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        setError(res.message || "Login failed");
      }
    } catch (err) {
      console.error("Login error:", err);

      setError(err.response?.data?.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // FORGOT PASSWORD
  // -------------------------
  const handleForgotPassword = async (e) => {
    e.preventDefault();

    setError("");
    setMessage("");

    const email = forgotEmail.trim().toLowerCase();

    if (!email) {
      setError("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      setLoading(true);

      const res = await forgotPassword(email);

      if (res.success) {
        setMessage(
          res.message ||
            "If an account exists with this email, a password reset link has been sent.",
        );
      } else {
        setError(res.message || "Unable to process request");
      }
    } catch (err) {
      console.error("Forgot password error:", err);

      setError(
        err.response?.data?.message ||
          "Unable to process forgot password request",
      );
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // TAB CHANGE
  // -------------------------
  const changeTab = (tab) => {
    setActiveTab(tab);
    setError("");
    setMessage("");
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-xl shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-black text-xl"
        >
          <FaTimes />
        </button>

        {/* Title */}
        <div className="text-center mb-5">
          <h2 className="text-2xl font-semibold text-light_primary">Welcome</h2>

          <p className="text-sm text-gray-500 mt-1">
            Access your Legacy Decor account
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => changeTab("login")}
            className={`w-1/2 py-3 text-sm font-medium transition ${
              activeTab === "login"
                ? "text-[#B88A3B] border-b-2 border-light_primary"
                : "text-gray-500"
            }`}
          >
            Login
          </button>

          <button
            type="button"
            onClick={() => changeTab("signup")}
            className={`w-1/2 py-3 text-sm font-medium transition ${
              activeTab === "signup"
                ? "text-[#B88A3B] border-b-2 border-light_primary"
                : "text-gray-500"
            }`}
          >
            Sign Up
          </button>
        </div>

        {/* =========================
            LOGIN FORM
        ========================= */}
        {activeTab === "login" && (
          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>

              <input
                type="email"
                placeholder="Enter your email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="text-black w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-[#B88A3B]"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>

              <input
                type="password"
                placeholder="Enter your password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="text-black w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-[#B88A3B]"
              />
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => {
                  setError("");
                  setMessage("");
                  setActiveTab("forgot");
                }}
                className="text-sm text-[#B88A3B] hover:underline"
              >
                Forgot Password?
              </button>
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                {error}
              </p>
            )}

            {/* Success */}
            {message && (
              <p className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded">
                {message}
              </p>
            )}

            {/* Login */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-light_primary text-white py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-60"
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        )}

        {/* =========================
            SIGN UP FORM
        ========================= */}
        {activeTab === "signup" && (
          <form onSubmit={handleRegister} className="flex flex-col gap-4">
            {/* Name */}
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={registerData.name}
              onChange={handleRegisterChange}
              className="text-black w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-[#B88A3B]"
            />

            {/* Email */}
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={registerData.email}
              onChange={handleRegisterChange}
              className="text-black w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-[#B88A3B]"
            />

            {/* Phone */}
            <input
              type="tel"
              name="phone"
              placeholder="10-digit Mobile Number"
              value={registerData.phone}
              onChange={handleRegisterChange}
              maxLength={10}
              inputMode="numeric"
              required
              className="text-black w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-[#B88A3B]"
            />

            {/* Password */}
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={registerData.password}
              onChange={handleRegisterChange}
              className="text-black w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-[#B88A3B]"
            />

            {/* Error */}
            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                {error}
              </p>
            )}

            {/* Success */}
            {message && (
              <p className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded">
                {message}
              </p>
            )}

            {/* Register */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-light_primary text-white py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-60"
            >
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          </form>
        )}

        {/* =========================
            FORGOT PASSWORD FORM
        ========================= */}
        {activeTab === "forgot" && (
          <form onSubmit={handleForgotPassword} className="flex flex-col gap-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">
                Forgot Password?
              </h3>

              <p className="text-sm text-gray-500 mt-1">
                Enter your registered email address and we'll send you a
                password reset link.
              </p>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>

              <input
                type="email"
                placeholder="Enter your email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="text-black w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-[#B88A3B]"
              />
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                {error}
              </p>
            )}

            {/* Success */}
            {message && (
              <p className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded">
                {message}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-light_primary text-white py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>

            {/* Back */}
            <button
              type="button"
              onClick={() => {
                setActiveTab("login");
                setError("");
                setMessage("");
              }}
              className="text-sm text-[#B88A3B] hover:underline"
            >
              Back to Login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
